/**
 * Move a vanity's GROUP-OWNED accessories — mirrors, sconces, overhead and
 * bar lights — after the body they belong over has moved or changed size.
 *
 * Lifted VERBATIM out of
 * `functions/.../designAutomation/placement/placeVanityAccessories.js`, which
 * now requires it back, so there is one definition. It was already pure math
 * over objects; what kept it in `functions/` was only where it happened to be
 * written. The client-side run engine needs it: a vanity-profile cabinet run
 * re-solves in the browser (a bay edit, a run-end drag, an appliance drop),
 * and the accessories over its basins have to follow without a round trip to
 * a cloud tool.
 *
 * The contract the callers rely on, unchanged:
 *
 * - It DECIDES, it does not apply. The return is `{ updates, removeIds }` —
 *   an id → replacement map and the ids that could not be placed at all. The
 *   caller writes them.
 * - An accessory is CULLED only for a collision the move CREATES. One that
 *   already sat badly (a mirror past the wall end, one grazing a linen tower)
 *   is not this pass's to delete.
 * - Before culling it NARROWS: a procedural mirror or light bar is a size,
 *   not a product, so 23.5" of mirror beats none. A catalog pick is a product
 *   and is never resized (`isResizableAccessory`) — it falls through to the
 *   cull.
 * - A light exists to light a MIRROR. When the mirror does not survive, its
 *   light does not either.
 *
 * Every helper it needs was already in this directory
 * (`checkWallFixtureOverlap`, `isResizableAccessory`, `isOverheadLight`,
 * `MIRROR_MIN_SIZE`); the cloud module had been reaching them through
 * `designAutomation/utils` and `designAutomation/rules`, both of which are
 * re-export shims onto exactly these files.
 */

const { INCHES_TO_METERS } = require('./constants');
const { MIRROR_MIN_SIZE } = require('./accessoryRules');
const { isResizableAccessory } = require('./accessoryRules');
const { checkWallFixtureOverlap } = require('./wallFixtureCollision');
const { isOverheadLight: isOverheadLightType } = require('./fixtureGroup');

// Search step when shrinking an accessory to clear an obstruction — the
// same 0.25" the sibling `vanity-yields-to-door` search uses.
const NARROW_STEP_M = 0.25 * INCHES_TO_METERS;

/**
 * Shift a wall-mounted fixture to a new wall projection while
 * preserving its perpendicular distance from the wall baseline. Used
 * by the resize path where a fixture's along-wall position tracks a
 * moving anchor (sink / mirror) but its depth offset is preserved.
 *
 * Pure — returns a new center, doesn't mutate input.
 */
function shiftToProjectionPreservingDepth({
  center,
  newProjection,
  wallStart,
  wallDir,
  wallNormal,
}) {
  const depthFromWall =
    (center.x - wallStart.x) * wallNormal.x +
    (center.z - wallStart.z) * wallNormal.z;
  return {
    x: wallStart.x + newProjection * wallDir.x + wallNormal.x * depthFromWall,
    y: center.y,
    z: wallStart.z + newProjection * wallDir.z + wallNormal.z * depthFromWall,
  };
}

/**
 * The widest this accessory can be at `center` and still clear everything,
 * or null if it can't be made to fit (or isn't ours to resize).
 *
 * A procedural mirror or light bar is a SIZE, not a product: when the move
 * pushes one into a linen tower by half an inch the answer is a slightly
 * narrower mirror, not no mirror. A catalog pick IS a product — never
 * resized (`isResizableAccessory`), so it falls through to the cull.
 */
function widthThatFits({ fixture, center, parentWall, roomData }) {
  if (!isResizableAccessory(fixture)) return null;
  const width = fixture.dimensions?.x;
  if (!Number.isFinite(width)) return null;
  for (
    let w = width - NARROW_STEP_M;
    w >= MIRROR_MIN_SIZE;
    w -= NARROW_STEP_M
  ) {
    const dims = { ...fixture.dimensions, x: w };
    if (!checkWallFixtureOverlap(center, dims, roomData, parentWall).collides) {
      return dims;
    }
  }
  return null;
}

/**
 * Collision-check a candidate center for a wall-mounted fixture; on
 * clear, add to the `updates` map; on collide, narrow it to fit if we can
 * and otherwise push to `removeIds`. Mutates the caller's collections in
 * place so the three resize-path passes don't drift from each other.
 */
function commitFixtureOrCull({
  fixture,
  newCenter,
  updates,
  removeIds,
  parentWall,
  roomData,
}) {
  if (parentWall && fixture.dimensions) {
    const collision = checkWallFixtureOverlap(
      newCenter,
      fixture.dimensions,
      roomData,
      parentWall
    );
    if (collision.collides) {
      // Cull only a collision the MOVE creates. An accessory that already
      // sat this way (a mirror 2" past the wall end, one grazing a linen
      // tower) is not the resize's to delete — a 2" depth change must not
      // cost a double vanity its second mirror and light.
      const before = fixture.center
        ? checkWallFixtureOverlap(
            fixture.center,
            fixture.dimensions,
            roomData,
            parentWall
          )
        : { collides: false };
      if (!before.collides) {
        // …and even then, shrink before deleting. haines Scan 1 bath
        // B88124DF lost its second 24" mirror outright because the resize
        // slid it 13mm into a linen tower; 23.5" of mirror was always the
        // better answer than none, and the light bar below it was left
        // hanging over nothing.
        const dims = widthThatFits({
          fixture,
          center: newCenter,
          parentWall,
          roomData,
        });
        if (!dims) {
          removeIds.push(fixture.id);
          return;
        }
        updates.set(fixture.id, {
          ...fixture,
          center: newCenter,
          dimensions: dims,
        });
        return;
      }
    }
  }
  updates.set(fixture.id, { ...fixture, center: newCenter });
}

/**
 * Reposition mirrors, wall lights, and bar lights after cabinet width/depth change.
 * Returns a Map of id → updated object, plus a list of ids to remove (collision).
 *
 * @param {Object} options
 * @param {Array}  options.mirrors
 * @param {Array}  options.wallLights
 * @param {Array}  options.vanityLights
 * @param {Array}  options.sinks - original sink positions
 * @param {number} options.centerShift - lateral shift along wall
 * @param {number} options.depthCenterShift - perpendicular shift
 * @param {boolean} options.isDoubleSink
 * @param {Object} options.parentWall
 * @param {Object} options.roomData
 * @param {Object} options.wallDir - { x, z }
 * @param {Object} options.wallNormal - { x, z }
 * @param {Object} options.wallStart - { x, z } bottomLeft
 * @param {Function} options.projectOntoWall
 * @param {Function} options.getUpdatedCenter - (id) => center (from already-updated objects)
 * @returns {{ updates: Map, removeIds: string[] }}
 */
function repositionAccessoriesToCabinet({
  mirrors,
  wallLights,
  vanityLights,
  sinks,
  centerShift,
  depthCenterShift,
  isDoubleSink,
  parentWall,
  roomData,
  wallDir,
  wallNormal,
  wallStart,
  projectOntoWall,
  getUpdatedCenter,
}) {
  const updates = new Map();
  const removeIds = [];

  // --- Mirrors ---
  for (const mirror of mirrors) {
    const mirrorProj = projectOntoWall(mirror.center);
    const vanityCenterProj = projectOntoWall(
      getUpdatedCenter(mirror.fixtureGroupId + '_vanity') || mirror.center
    );

    let newCenter;
    if (isDoubleSink && sinks.length >= 2) {
      const sortedSinks = [...sinks].sort(
        (a, b) => projectOntoWall(a.center) - projectOntoWall(b.center)
      );
      const isLeftMirror = mirrorProj < vanityCenterProj;
      const associatedSink = isLeftMirror
        ? sortedSinks[0]
        : sortedSinks[sortedSinks.length - 1];

      const sinkNewCenter = getUpdatedCenter(associatedSink.id);
      if (sinkNewCenter) {
        const originalOffset =
          mirrorProj - projectOntoWall(associatedSink.center);
        const newMirrorProj = projectOntoWall(sinkNewCenter) + originalOffset;
        newCenter = shiftToProjectionPreservingDepth({
          center: mirror.center,
          newProjection: newMirrorProj,
          wallStart,
          wallDir,
          wallNormal,
        });
      }
    }

    if (!newCenter) {
      newCenter = {
        x: mirror.center.x + wallDir.x * centerShift,
        y: mirror.center.y,
        z: mirror.center.z + wallDir.z * centerShift,
      };
    }

    commitFixtureOrCull({
      fixture: mirror,
      newCenter,
      updates,
      removeIds,
      parentWall,
      roomData,
    });
  }

  // A vanity light exists to light a MIRROR (one bar per mirror, both the
  // scan and `optimize_vanity` paths). When a mirror doesn't survive, its
  // light must not either: `getUpdatedCenter` falls back to the culled
  // mirror's PRE-move center, so the bar used to stay behind — lighting
  // nothing, 2" off the basin it no longer belongs to (haines Scan 1).
  const culled = new Set(removeIds);
  const cullWithMirror = (fixture, associatedMirror) => {
    if (!associatedMirror || !culled.has(associatedMirror.id)) return false;
    removeIds.push(fixture.id);
    return true;
  };

  // --- Wall lights (sconces and overhead) ---
  for (const light of wallLights) {
    const lightProj = projectOntoWall(light.center);
    const isOverhead = isOverheadLightType(light);

    let newCenter;
    if (isOverhead) {
      const associatedMirror = findClosestByProj(
        mirrors,
        lightProj,
        projectOntoWall
      );
      if (cullWithMirror(light, associatedMirror)) continue;
      if (associatedMirror) {
        const mirrorNewCenter =
          updates.get(associatedMirror.id)?.center ||
          getUpdatedCenter(associatedMirror.id);
        if (mirrorNewCenter) {
          newCenter = shiftToProjectionPreservingDepth({
            center: light.center,
            newProjection: projectOntoWall(mirrorNewCenter),
            wallStart,
            wallDir,
            wallNormal,
          });
        }
      }
    } else if (isDoubleSink && mirrors.length >= 2) {
      // Flanking sconce — track the closest mirror, preserve offset.
      const closestMirror = findClosestByProj(
        mirrors,
        lightProj,
        projectOntoWall
      );
      if (cullWithMirror(light, closestMirror)) continue;
      if (closestMirror) {
        const mirrorNewCenter =
          updates.get(closestMirror.id)?.center ||
          getUpdatedCenter(closestMirror.id);
        if (mirrorNewCenter) {
          const originalOffset =
            lightProj - projectOntoWall(closestMirror.center);
          newCenter = shiftToProjectionPreservingDepth({
            center: light.center,
            newProjection: projectOntoWall(mirrorNewCenter) + originalOffset,
            wallStart,
            wallDir,
            wallNormal,
          });
        }
      }
    }

    if (!newCenter) {
      newCenter = {
        x: light.center.x + wallDir.x * centerShift,
        y: light.center.y,
        z: light.center.z + wallDir.z * centerShift,
      };
    }

    commitFixtureOrCull({
      fixture: light,
      newCenter,
      updates,
      removeIds,
      parentWall,
      roomData,
    });
  }

  // --- Bar lights ---
  for (const barLight of vanityLights) {
    const barLightProj = projectOntoWall(barLight.center);
    const associatedMirror = findClosestByProj(
      mirrors,
      barLightProj,
      projectOntoWall
    );
    if (cullWithMirror(barLight, associatedMirror)) continue;

    let newCenter;
    if (associatedMirror) {
      const mirrorNewCenter =
        updates.get(associatedMirror.id)?.center ||
        getUpdatedCenter(associatedMirror.id);
      if (mirrorNewCenter) {
        newCenter = shiftToProjectionPreservingDepth({
          center: barLight.center,
          newProjection: projectOntoWall(mirrorNewCenter),
          wallStart,
          wallDir,
          wallNormal,
        });
      }
    }
    if (!newCenter) {
      newCenter = {
        x: barLight.center.x + wallDir.x * centerShift,
        y: barLight.center.y,
        z: barLight.center.z + wallDir.z * centerShift,
      };
    }

    commitFixtureOrCull({
      fixture: barLight,
      newCenter,
      updates,
      removeIds,
      parentWall,
      roomData,
    });
  }

  return { updates, removeIds };
}

/**
 * The item whose along-wall projection is nearest `targetProj`, or null.
 * Shared with the mirror-swap pass in the cloud module that used to own it.
 */
function findClosestByProj(items, targetProj, projectFn) {
  if (!items || items.length === 0) return null;
  let closest = items[0];
  let closestDist = Math.abs(projectFn(items[0].center) - targetProj);
  for (let i = 1; i < items.length; i++) {
    const dist = Math.abs(projectFn(items[i].center) - targetProj);
    if (dist < closestDist) {
      closestDist = dist;
      closest = items[i];
    }
  }
  return closest;
}

module.exports = {
  repositionAccessoriesToCabinet,
  shiftToProjectionPreservingDepth,
  widthThatFits,
  commitFixtureOrCull,
  findClosestByProj,
  NARROW_STEP_M,
};
